/**
 * OnePagerTable
 * High-performance virtualized table for the KPI One Pager V2.
 *
 * Architecture:
 * - useScrollSync hook for scroll synchronization
 * - OnePagerRow (memoized) for KPI data cells
 * - StickyRow (memoized) for grain dimension cells
 * - formatKpiValue from shared formatters
 *
 * • Receives all state through props
 * • Exposes callbacks only
 * • No Redux inside the component
 */

import React, { useCallback, useMemo } from 'react';
import { ActivityIndicator, FlatList, Platform, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import {
	GrainKey,
	GroupedRow,
	PagerColumn,
	SparcCategory,
	TableSortState,
	getCategorySpans,
	getGrainLabel,
	SPARC_TAGS,
} from '../../constants/onePagerConstants';
import { createOnePagerTableStyles, ROW_HEIGHT, GRAIN_COL_WIDTH, KPI_COL_WIDTH, BODY_CONTAINER_HEIGHT, MAX_VISIBLE_ROWS } from '../../styles/onePagerTableStyles';
import { useTheme } from '../../../../theme/useTheme';
import { formatKpiValue, formatTargetValue } from '../../utils/onePagerFormatters';
import { isMetricBad } from '../../utils/onePagerStatus';
import { useScrollSync } from '../../hooks/useScrollSync';
import type { NavigableFilterType } from '../../types/onePagerTypes';
import OnePagerRow from './OnePagerRow';
import StickyRow from './StickyRow';
import KpiTargetTooltip from '../KpiTargetTooltip';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface OnePagerTableProps {
	readonly rows: GroupedRow[];
	readonly grandTotalRow: GroupedRow | null;
	readonly grainSelections: GrainKey[];
	readonly columns: PagerColumn[];
	readonly sort: TableSortState;
	readonly loading: boolean;
	readonly onSort: (col: string) => void;
	readonly onNavigateToOverview: (filterType: NavigableFilterType, value: string) => void;
	readonly onExport?: () => void;
	readonly isExporting?: boolean;
	// Pagination props (optional — table remains presentational)
	readonly totalRows?: number;
	readonly totalPages?: number;
	readonly page?: number;
	readonly pageSize?: number;
	readonly onPageChange?: (page: number) => void;
	readonly onPageSizeChange?: (size: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OnePagerTable: React.FC<OnePagerTableProps> = ({
	rows,
	grandTotalRow,
	grainSelections,
	columns,
	sort,
	loading,
	onSort,
	onNavigateToOverview,
	onExport,
	isExporting = false,
	totalRows,
	totalPages,
	page,
	pageSize,
	onPageChange,
	onPageSizeChange,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const styles = createOnePagerTableStyles(isDark, colors);
	const { refs, handlers } = useScrollSync();

	// ─── SPARC Category Style Lookup (inline — depends on styles from createOnePagerTableStyles) ──
	const getCategoryStyle = (sparc: SparcCategory) => {
		switch (sparc) {
			case 'S': return styles.categoryCellS;
			case 'P': return styles.categoryCellP;
			case 'A': return styles.categoryCellA;
			case 'R': return styles.categoryCellR;
			case 'C': return styles.categoryCellC;
			case 'N': return styles.categoryCellN;
			default: return undefined;
		}
	};

	const getCategoryTextStyle = (sparc: SparcCategory) => {
		switch (sparc) {
			case 'S': return styles.categoryTextS;
			case 'P': return styles.categoryTextP;
			case 'A': return styles.categoryTextA;
			case 'R': return styles.categoryTextR;
			case 'C': return styles.categoryTextC;
			case 'N': return styles.categoryTextN;
			default: return undefined;
		}
	};

	// ─── Derived calculations (memoized) ────────────────────────────────
	const categorySpans = useMemo(() => getCategorySpans(), []);
	const stickyWidth = useMemo(() => grainSelections.length * GRAIN_COL_WIDTH, [grainSelections.length]);
	const totalKpiWidth = useMemo(() => columns.length * KPI_COL_WIDTH, [columns.length]);
	const contentWidthStyle = useMemo(() => ({ width: totalKpiWidth }), [totalKpiWidth]);

	// Dynamic body height: shrink to fit when rows <= MAX_VISIBLE_ROWS, cap otherwise
	const bodyContainerStyle = useMemo(
		() => [styles.bodyContainer, { height: Math.min(rows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT }],
		[rows.length],
	);

	/** Set of column indices that are the last column in their SPARC category */
	const sparcBoundaryIndices: ReadonlySet<number> = useMemo(() => {
		const boundaries = new Set<number>();
		for (let i = 0; i < columns.length - 1; i++) {
			if (columns[i].sparc !== columns[i + 1].sparc) {
				boundaries.add(i);
			}
		}
		return boundaries;
	}, [columns]);

	// ─── FlatList optimizations ─────────────────────────────────────────
	const getItemLayout = useCallback(
		(_: unknown, index: number) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index }),
		[],
	);

	const keyExtractor = useCallback(
		(item: GroupedRow, index: number) => {
			const key = grainSelections.map((k) => item.grainVals[k] || '').join('|');
			return `${key}-${index}`;
		},
		[grainSelections],
	);

	// ─── Render callbacks (stable references) ───────────────────────────
	const renderStickyItem = useCallback(
		({ item, index }: { item: GroupedRow; index: number }) => (
			<StickyRow
				row={item}
				index={index}
				grainSelections={grainSelections}
				onNavigateToOverview={onNavigateToOverview}
			/>
		),
		[grainSelections, onNavigateToOverview],
	);

	const renderBodyItem = useCallback(
		({ item, index }: { item: GroupedRow; index: number }) => (
			<OnePagerRow row={item} index={index} columns={columns} sparcBoundaryIndices={sparcBoundaryIndices} />
		),
		[columns, sparcBoundaryIndices],
	);

	// ─── Loading state ──────────────────────────────────────────────────
	if (loading && rows.length === 0) {
		return (
			<View style={styles.container}>
				<View style={styles.loadingContainer}>
					<ActivityIndicator size="large" color="#005CBC" />
					<Text style={styles.loadingText}>Loading data...</Text>
				</View>
			</View>
		);
	}

	// ─── Empty state ────────────────────────────────────────────────────
	if (!loading && rows.length === 0) {
		return (
			<View style={styles.container}>
				<View style={styles.emptyContainer}>
					<MaterialIcons name="table-chart" size={36} color="#ADB5BD" />
					<Text style={styles.emptyText}>No data available for the current configuration.</Text>
				</View>
			</View>
		);
	}

	// ─── Render ─────────────────────────────────────────────────────────
	return (
		<View style={styles.container}>
			{/* ═══ SECTION HEADER ═══ */}
			<View style={styles.sectionHeader}>
				<View style={styles.sectionHeaderLeft}>
					<Text style={styles.sectionTitle}>Location Details</Text>
					<Text style={styles.sectionHint}>Region / Unit level. Expand rows to drill to Market and Location.</Text>
				</View>
				{onExport && (
					<TouchableOpacity
						style={[styles.exportButton, isExporting && styles.exportButtonDisabled]}
						onPress={onExport}
						disabled={isExporting}
						activeOpacity={0.7}
						accessibilityLabel="Export table data"
					>
						{isExporting ? (
							<>
								<ActivityIndicator size="small" color="#034895" style={styles.exportButtonSpinner} />
								<Text style={styles.exportButtonText}>Exporting...</Text>
							</>
						) : (
							<>
								<MaterialIcons name="file-download" size={16} color="#034895" />
								<Text style={styles.exportButtonText}>Export</Text>
							</>
						)}
					</TouchableOpacity>
				)}
			</View>

			<View style={styles.tableWrapper}>
			{/* ═══ STICKY LEFT COLUMN ═══ */}
			<View style={[styles.stickyColumn, { width: stickyWidth }]}>
				<View style={[styles.stickyHeaderRow, styles.stickyHeaderWrapper]}>
					{grainSelections.map((grainKey) => {
						// Each grain header sorts by its own dimension. Previously every
						// header dispatched the shared 'geo' literal, so all of them
						// triggered the same action and lit up simultaneously.
						const isActiveSort = sort.col === grainKey;
						return (
							<TouchableOpacity
								key={grainKey}
								style={styles.stickyHeaderCell}
								onPress={() => onSort(grainKey)}
								activeOpacity={0.7}
								accessibilityLabel={`Sort by ${getGrainLabel(grainKey)}`}
							>
								<Text style={styles.stickyHeaderText}>{getGrainLabel(grainKey)}</Text>
								<MaterialIcons
									name={isActiveSort ? (sort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
									size={14}
									color={isActiveSort ? '#005CBC' : '#9E9E9E'}
									style={styles.sortIcon}
								/>
							</TouchableOpacity>
						);
					})}

				</View>

					{grandTotalRow && (
						<View style={styles.grandTotalStickyRow}>
							{grainSelections.map((grainKey, idx) => (
								<View key={grainKey} style={styles.grandTotalCell}>
									<Text style={styles.grandTotalCellText}>{idx === 0 ? 'Grand Total' : ''}</Text>
								</View>
							))}
						</View>
					)}

					<View style={bodyContainerStyle}>
						<FlatList
							ref={refs.stickyListRef}
							data={rows}
							renderItem={renderStickyItem}
							keyExtractor={keyExtractor}
							getItemLayout={getItemLayout}
							onScroll={handlers.onStickyVerticalScroll}
							scrollEventThrottle={16}
							showsVerticalScrollIndicator={false}
							removeClippedSubviews
							maxToRenderPerBatch={20}
							windowSize={5}
							initialNumToRender={15}
						/>
					</View>
				</View>

				{/* ═══ SCROLLABLE KPI AREA ═══ */}
				<View style={styles.scrollableArea}>
					{/* Header (sticky on vertical scroll) */}
					<View style={styles.stickyHeaderWrapper}>
						<ScrollView
							ref={refs.headerScrollRef}
							horizontal
							showsHorizontalScrollIndicator={false}
							scrollEventThrottle={16}
							onScroll={handlers.onHeaderHorizontalScroll}
							bounces={false}
							contentContainerStyle={contentWidthStyle}
						>
							<View>
								<View style={styles.categoryHeaderRow}>
									{categorySpans.map((span, idx) => (
										<View
											key={`${span.sparc}-${idx}`}
											style={[
												styles.categoryCell,
												getCategoryStyle(span.sparc),
												{ width: span.count * KPI_COL_WIDTH },
											]}
										>
											<Text style={[styles.categoryText, getCategoryTextStyle(span.sparc)]}>
												{SPARC_TAGS[span.sparc]}
											</Text>
										</View>
									))}
								</View>

								<View style={styles.columnHeaderRow}>
									{columns.map((col, colIdx) => {
										const isActiveSort = sort.col === col.id;
										const isBoundary = sparcBoundaryIndices.has(colIdx);
										return (
											<TouchableOpacity
												key={col.id}
												style={[styles.columnHeaderCell, isBoundary && styles.sparcBorderRight]}
												onPress={() => onSort(col.id)}
												activeOpacity={0.7}
												accessibilityLabel={`Sort by ${col.label}`}
											>
												<View style={styles.columnHeaderTextWrap}>
												<Text style={styles.columnHeaderText} numberOfLines={2}>{col.label}</Text>
												</View>
												<MaterialIcons
													name={isActiveSort ? (sort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
													size={14}
													color={isActiveSort ? '#005CBC' : '#9E9E9E'}
													style={styles.sortIcon}
												/>
											</TouchableOpacity>
										);
									})}
								</View>
							</View>
						</ScrollView>
					</View>

					{/* Grand Total (below header, above data rows) */}
					{grandTotalRow && (
						<ScrollView
							ref={refs.grandTotalScrollRef}
							horizontal
							showsHorizontalScrollIndicator={false}
							scrollEnabled={false}
							contentContainerStyle={contentWidthStyle}
						>
							<View style={styles.grandTotalRow}>
								{columns.map((col, colIdx) => {
									const targetVal = grandTotalRow.targets?.[col.id];
									// Grand total reads flags exactly as a data row — same rules, no
									// special-casing. Bold comes from grandTotalKpiText; danger colour
									// is layered last so it wins while the bold weight survives.
									const isBad = isMetricBad(grandTotalRow, col.id);
									return (
										<View key={col.id} style={[styles.grandTotalKpiCell, sparcBoundaryIndices.has(colIdx) && styles.sparcBorderRight]}>
											<KpiTargetTooltip
												metricName={col.label}
												actualValue={formatKpiValue(grandTotalRow.vals[col.id], col)}
												targetValue={targetVal !== undefined ? formatTargetValue(targetVal, col) : null}
												varianceActualValue={col.id === 'gap' ? grandTotalRow.vals[col.id] ?? null : undefined}
											>
												<Text
													style={[
														styles.grandTotalKpiText,
														isBad && styles.kpiCellDanger,
													]}
													numberOfLines={1}
												>
													{formatKpiValue(grandTotalRow.vals[col.id], col)}
												</Text>
											</KpiTargetTooltip>
										</View>
									);
								})}
							</View>
						</ScrollView>
					)}

					{/* Body */}
					<View style={bodyContainerStyle}>
						<ScrollView
							ref={refs.bodyScrollRef}
							horizontal
							showsHorizontalScrollIndicator
							scrollEventThrottle={16}
							onScroll={handlers.onBodyHorizontalScroll}
							bounces={false}
							contentContainerStyle={contentWidthStyle}
						>
							<FlatList
								ref={refs.bodyListRef}
								data={rows}
								renderItem={renderBodyItem}
								keyExtractor={keyExtractor}
								getItemLayout={getItemLayout}
								onScroll={handlers.onBodyVerticalScroll}
								scrollEventThrottle={16}
								showsVerticalScrollIndicator
								removeClippedSubviews
								maxToRenderPerBatch={20}
								windowSize={5}
								initialNumToRender={15}
							/>
						</ScrollView>
					</View>
				</View>
			</View>

			{/* ═══ PAGINATION BAR ═══ */}
			{totalRows != null && page != null && pageSize != null && onPageChange && (
				<View style={styles.paginationBar}>
					<Text style={styles.paginationInfo}>
						{`Showing ${Math.min((page - 1) * pageSize + 1, totalRows)}–${Math.min(page * pageSize, totalRows)} of ${totalRows}`}
					</Text>
					<View style={styles.paginationControls}>
						<TouchableOpacity
							style={[styles.paginationButton, page <= 1 && styles.paginationButtonDisabled]}
							onPress={() => onPageChange(page - 1)}
							disabled={page <= 1}
							activeOpacity={0.7}
							accessibilityLabel="Previous page"
						>
							<MaterialIcons name="chevron-left" size={20} color={page <= 1 ? '#CED4DA' : '#005CBC'} />
						</TouchableOpacity>
						<Text style={styles.paginationPageText}>{`Page ${page} of ${totalPages ?? 1}`}</Text>
						<TouchableOpacity
							style={[styles.paginationButton, page * pageSize >= totalRows && styles.paginationButtonDisabled]}
							onPress={() => onPageChange(page + 1)}
							disabled={page * pageSize >= totalRows}
							activeOpacity={0.7}
							accessibilityLabel="Next page"
						>
							<MaterialIcons name="chevron-right" size={20} color={page * pageSize >= totalRows ? '#CED4DA' : '#005CBC'} />
						</TouchableOpacity>
					</View>
					{onPageSizeChange && (
						<View style={styles.paginationSizeSelector}>
							{[25, 50, 100].map((size) => (
								<TouchableOpacity
									key={size}
									style={[styles.paginationSizeOption, pageSize === size && styles.paginationSizeOptionActive]}
									onPress={() => onPageSizeChange(size)}
									activeOpacity={0.7}
								>
									<Text style={[styles.paginationSizeText, pageSize === size && styles.paginationSizeTextActive]}>
										{size}
									</Text>
								</TouchableOpacity>
							))}
						</View>
					)}
				</View>
			)}
		</View>
	);
};

OnePagerTable.displayName = 'OnePagerTable';

export default React.memo(OnePagerTable);
